import i18n from '@/utils/i18n';
import InvoiceBadgedIcon from '@assets/icons/invoice-badged.svg';
import InvoiceIcon from '@assets/icons/invoice.svg';
import React, { useMemo } from 'react';
import { canDisplayETABanner, OrderETABanner } from './order-eta-banner';
import { ConsumptionTax } from '@/contexts/dynamic-config';
import { createStyles } from '@/contexts/theme-context';
import { displayInvoiceTotal } from '@/utils/invoice';
import { displayTotalPrice } from '@/utils/money';
import { FONTS } from '@/styles';
import { formatDate } from '@/utils/date';
import { getOrderDate, isPastDateOrToday } from '@/utils/order-helpers';
import { HistoryListItem } from '@/components/history-list-item';
import {
  OrderHistoryListOrderFeesFieldsFragment,
  OrderHistoryListOrderFieldsFragment,
} from '@/types';
import { OrderStatusLabel } from './order-status-label';
import { Platform, View } from 'react-native';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { usePaymentsContext } from '@/contexts/payments-context';

export const FOOTER_MARGIN = 8;

interface Props {
  consumptionTax?: ConsumptionTax[] | null;
  displayItemPrices: boolean;
  onPress: (orderUUID: string) => void;
  order: OrderHistoryListOrderFieldsFragment;
  orderFees: OrderHistoryListOrderFeesFieldsFragment[];
}

export const OrderHistoryListItem: React.FC<Props> = ({
  consumptionTax,
  displayItemPrices,
  onPress,
  order,
  orderFees,
}) => {
  const [styles, theme] = useStyles();
  const { currencyCode, useV2Invoices } = usePaymentsContext();
  const checkInConfig = useDynamicConfigValue('check_in_config');

  // Determine the invoice from either ther FK on order_invoices or
  // the order_invoices_orders through table
  const invoice:
    | OrderHistoryListOrderFieldsFragment['order_invoices'][0]
    | undefined = // Type hint is required for TS to understand this may be undefined
    order.order_invoices[0] || order.order_invoices_orders[0]?.order_invoice;

  const isTodayOrPastOrder = useMemo(() => {
    const orderDate = getOrderDate(order);
    return isPastDateOrToday(orderDate);
  }, [order]);

  const showCheckInStatusLabel =
    isTodayOrPastOrder &&
    Platform.OS !== 'web' &&
    checkInConfig?.check_in_enabled &&
    Boolean(invoice) &&
    useV2Invoices;

  const InvoiceIconComponent = invoice?.seen ? InvoiceIcon : InvoiceBadgedIcon;

  /**
   * Determines the order total copy to display (including currency code)
   *
   * If the order has an associated invoice, and the distributor is on V2,
   * use the summed invoice line items as the total.
   *
   * Otherwise use the order fees view.
   */
  const priceText = useMemo(() => {
    if (useV2Invoices && invoice) {
      return displayInvoiceTotal(
        invoice.order_invoice_line_items,
        currencyCode,
        consumptionTax,
      );
    } else {
      return displayTotalPrice(
        {
          orderFees,
          orderItems: order.orders_items,
        },
        {
          // Hide prices if there is no invoice and the supplier disables them
          displayItemPrices: Boolean(invoice) || displayItemPrices,
          displayPlaceholder: false,
          hideMissingItems: Boolean(invoice),
        },
      );
    }
  }, [
    consumptionTax,
    currencyCode,
    invoice,
    order,
    orderFees,
    useV2Invoices,
    displayItemPrices,
  ]);

  const orderTracking = order.order_tracking || null;
  const existingCheckIn = invoice?.order_invoice_check_in;

  return (
    <HistoryListItem
      bottomLeftComponent={
        <OrderStatusLabel
          existingCheckIn={existingCheckIn}
          labelStyle={styles.bottomRowText}
          order={order}
          showCheckInStatusLabel={showCheckInStatusLabel}
        />
      }
      bottomRightText={
        invoice
          ? i18n.t('invoice_number', {
              number: invoice.invoice_number,
            })
          : undefined
      }
      footer={
        canDisplayETABanner(order) ? (
          <View style={styles.footer}>
            <OrderETABanner orderTracking={order.order_tracking!} />
          </View>
        ) : undefined
      }
      onPress={() => onPress(order.uuid)}
      showTopRightIcon={Boolean(invoice)}
      testID="order-list-item"
      topLeftText={
        formatDate(
          orderTracking?.order_completed_delivery_datetime ||
            order.supplier_accepted_delivery_time ||
            order.restaurant_desired_delivery_time,
        ) || ''
      }
      topRightIcon={<InvoiceIconComponent accentFill={theme.colors.accent} />}
      topRightText={priceText}
    />
  );
};

const useStyles = createStyles((theme) => ({
  bottomRowText: {
    ...FONTS.primaryBold,
    color: theme.colors.inactiveText,
    fontSize: theme.fontSizes.xsmall,
    textTransform: 'uppercase',
  },
  footer: {
    marginBottom: FOOTER_MARGIN,
    marginHorizontal: FOOTER_MARGIN,
  },
}));
